// Pokémon animado no PixiJS: idle/walk/attack/hurt em 8 direções (folhas do
// Mystery Dungeon). Enquanto a folha não carrega, mostra o sprite estático.
import { Container, Sprite, type Texture } from "pixi.js";
import { animNow, loadAnim, texture, type AnimName, type LoadedAnim } from "../sprites";

/** Direções PMD: 0 baixo, 1 baixo-dir., 2 dir., 3 cima-dir., 4 cima, 5 cima-esq., 6 esq., 7 baixo-esq. */
const BY_OCTANT = [2, 1, 0, 7, 6, 5, 4, 3];
export function dirFrom(dx: number, dy: number): number {
  const oct = Math.round(Math.atan2(dy, dx) / (Math.PI / 4));
  return BY_OCTANT[(oct + 8) % 8]!;
}

export class PokemonSprite extends Container {
  readonly body = new Sprite();
  private anim: LoadedAnim | null = null;
  private current: AnimName = "idle";
  private dir = 0;
  private frame = 0;
  private elapsed = 0;
  private oneShot: { onHit?: () => void; hitFired: boolean } | null = null;
  private scaleK = 1;

  /**
   * @param size lado da casa (px); define a escala.
   * @param flipStatic espelha o sprite estático (fallback), que só olha para um lado
   */
  constructor(private species: number, private shiny: boolean, private size: number, private flipStatic = false) {
    super();
    this.addChild(this.body);
    const ready = animNow(species, shiny);
    if (ready) this.useAnim(ready);
    else {
      this.useStatic();
      void loadAnim(species, shiny).then((a) => {
        if (a && !this.destroyed) this.useAnim(a);
      });
    }
  }

  private useStatic() {
    const tex = texture(this.species, this.shiny);
    if (tex) this.body.texture = tex;
    this.body.anchor.set(0.5, 0.72);
    this.body.scale.set((this.size * 1.25) / 96);
    if (this.flipStatic) this.body.scale.x *= -1;
  }

  private useAnim(a: LoadedAnim) {
    this.anim = a;
    const idle = a.info.anims.idle!;
    // PMD: todos na mesma escala (o tamanho relativo entre espécies é mantido:
    // Magikarp pequeno, Onix grande); só os muito grandes encolhem para caber
    this.scaleK = Math.min(this.size / 38, (this.size * 1.7) / Math.max(idle.w, idle.h));
    this.body.anchor.set(0.5, 0.62);
    this.body.scale.set(this.scaleK);
    this.applyFrame();
  }

  get animated(): boolean {
    return this.anim !== null;
  }

  setTint(color: number) {
    this.body.tint = color;
  }

  face(dx: number, dy: number) {
    if (dx === 0 && dy === 0) return;
    this.setDir(dirFrom(dx, dy));
  }

  setDir(dir: number) {
    if (dir === this.dir) return;
    this.dir = dir;
    this.applyFrame();
  }

  /** Troca para uma animação em loop (idle/walk). */
  loop(name: "idle" | "walk") {
    if (this.oneShot || this.current === name) return;
    this.start(name);
  }

  /** Toca uma animação uma vez (attack/hurt) e volta para idle; onHit no quadro de impacto. */
  play(name: "attack" | "hurt", onHit?: () => void) {
    if (!this.anim?.frames[name]) {
      onHit?.();
      return;
    }
    if (name === "hurt" && this.oneShot && this.current === "attack") return; // não interrompe o ataque
    this.oneShot = { onHit, hitFired: false };
    this.start(name);
  }

  private start(name: AnimName) {
    this.current = name;
    this.frame = 0;
    this.elapsed = 0;
    this.applyFrame();
  }

  private frames(): Texture[] | undefined {
    const f = this.anim?.frames[this.current] ?? this.anim?.frames.idle;
    if (!f) return undefined;
    return f[f.length === 1 ? 0 : this.dir];
  }

  private applyFrame() {
    const list = this.frames();
    const tex = list?.[Math.min(this.frame, list.length - 1)];
    if (tex) this.body.texture = tex;
  }

  update(dtMs: number) {
    if (!this.anim) return;
    const info = this.anim.info.anims[this.current] ?? this.anim.info.anims.idle!;
    this.elapsed += dtMs;
    let changed = false;
    for (;;) {
      const dur = ((info.durations[this.frame] ?? 4) * 1000) / 60;
      if (this.elapsed < dur) break;
      this.elapsed -= dur;
      this.frame++;
      changed = true;
      if (this.oneShot && !this.oneShot.hitFired && info.hit !== undefined && this.frame >= info.hit) {
        this.oneShot.hitFired = true;
        this.oneShot.onHit?.();
      }
      if (this.frame >= info.durations.length) {
        if (this.oneShot) {
          const cb = this.oneShot;
          this.oneShot = null;
          if (!cb.hitFired) cb.onHit?.();
          this.current = "idle";
        }
        this.frame = 0;
      }
    }
    if (changed) this.applyFrame();
  }
}
